import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router";
import { useForgotPassword } from "@/lib/hooks";

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage(): React.ReactElement {
  const forgotMutation = useForgotPassword();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData): Promise<void> => {
    try {
      await forgotMutation.mutateAsync(data.email);
    } catch {
      // Swallowed here; forgotMutation.isError drives the retry message below.
    }
  };

  // Success only. The SERVER already answers 200 whether or not the address
  // exists, so an error here is a real failure (network, rate limit, 5xx) and
  // nothing was sent — claiming otherwise leaves the vendor waiting for an
  // email that is not coming.
  const submitted = forgotMutation.isSuccess;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-[400px]">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="mb-2 text-center text-2xl font-bold text-gray-900">
            Forgot Password
          </h1>
          <p className="mb-6 text-center text-sm text-gray-500">
            Enter your email and we'll send you a reset link.
          </p>

          {submitted ? (
            <div className="space-y-4">
              <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                If an account exists with that email, you'll receive a password
                reset link.
              </div>
              <p className="text-center text-sm">
                <Link
                  to="/login"
                  className="text-blue-600 hover:text-blue-800"
                >
                  Back to login
                </Link>
              </p>
            </div>
          ) : (
            <>
              <form
                onSubmit={(e) => void handleSubmit(onSubmit)(e)}
                className="space-y-4"
              >
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    {...register("email")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {forgotMutation.isError && (
                  <p role="alert" className="text-sm text-red-600">
                    We could not send the reset link. Please try again in a moment.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || forgotMutation.isPending}
                  className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {forgotMutation.isPending
                    ? "Sending..."
                    : "Send Reset Link"}
                </button>
              </form>

              <p className="mt-4 text-center text-sm">
                <Link
                  to="/login"
                  className="text-blue-600 hover:text-blue-800"
                >
                  Back to login
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
